import { z } from 'zod';

export const ReferralStatusSchema = z.enum([
  'PENDING',
  'BOOKING_COMPLETED',
  'CREDITED',
  'CANCELLED',
  'EXPIRED',
]);

export type ReferralStatus = z.infer<typeof ReferralStatusSchema>;

export const ReferralSchema = z.object({
  id: z.string().uuid(),
  referrerId: z.string().uuid(),
  refereeId: z.string().uuid(),
  status: ReferralStatusSchema,
  rewardAmount: z.number().optional(),
  creditHeldUntil: z.string().datetime().optional(),
  completedAt: z.string().datetime().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type Referral = z.infer<typeof ReferralSchema>;

export const UserReferralStatsSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  referralCode: z.string(),
  totalReferrals: z.number().nonnegative(),
  successfulReferrals: z.number().nonnegative(),
  currentTier: z.string(),
  milestonesAchieved: z.array(z.number()),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type UserReferralStats = z.infer<typeof UserReferralStatsSchema>;

export const ReferralStatsResponseSchema = z.object({
  referralCode: z.string(),
  referralLink: z.string().url(),
  totalReferrals: z.number(),
  successfulReferrals: z.number(),
  pendingReferrals: z.number(),
  currentTier: z.object({
    name: z.string(),
    rewardMultiplier: z.number(),
  }),
  nextTier: z
    .object({
      name: z.string(),
      referralsNeeded: z.number(),
    })
    .optional(),
  nextMilestone: z
    .object({
      threshold: z.number(),
      bonusAmount: z.number(),
      referralsNeeded: z.number(),
    })
    .optional(),
  walletBalance: z.number(),
});

export type ReferralStatsResponse = z.infer<typeof ReferralStatsResponseSchema>;

export const ReferralDetailSchema = z.object({
  id: z.string().uuid(),
  refereeName: z.string(),
  status: ReferralStatusSchema,
  rewardAmount: z.number().optional(),
  completedAt: z.string().datetime().optional(),
  createdAt: z.string().datetime(),
});

export type ReferralDetail = z.infer<typeof ReferralDetailSchema>;

export const ReferralListResponseSchema = z.object({
  referrals: z.array(ReferralDetailSchema),
  pagination: z.object({
    page: z.number(),
    limit: z.number(),
    total: z.number(),
  }),
});

export type ReferralListResponse = z.infer<typeof ReferralListResponseSchema>;

// ============================================================================
// REFERRAL CONFIG (Admin)
// ============================================================================

export const ReferralConfigSchema = z.object({
  id: z.string().uuid(),
  cityId: z.string().uuid().nullable(),
  referrerCredit: z.number(),
  refereeDiscount: z.number(),
  minBookingAmount: z.number(),
  creditExpiryDays: z.number(),
  creditHoldDays: z.number(),
  isActive: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type ReferralConfig = z.infer<typeof ReferralConfigSchema>;

export const CreateReferralConfigDtoSchema = z.object({
  cityId: z.string().uuid().optional(),
  referrerCredit: z.number().min(0),
  refereeDiscount: z.number().min(0),
  minBookingAmount: z.number().min(0),
  creditExpiryDays: z.number().min(1).default(180),
  creditHoldDays: z.number().min(0).default(7),
});

export type CreateReferralConfigDto = z.infer<typeof CreateReferralConfigDtoSchema>;

export const UpdateReferralConfigDtoSchema = z.object({
  referrerCredit: z.number().min(0).optional(),
  refereeDiscount: z.number().min(0).optional(),
  minBookingAmount: z.number().min(0).optional(),
  creditExpiryDays: z.number().min(1).optional(),
  creditHoldDays: z.number().min(0).optional(),
  isActive: z.boolean().optional(),
});

export type UpdateReferralConfigDto = z.infer<typeof UpdateReferralConfigDtoSchema>;
